import type { LegacyTraceData, TraceResult } from '../../services/tracking.service';

/**
 * gwiTrace EXPORT trace -> the legacy popup's data shape.
 *
 * POH export moved from LYNX to gwiTrace on 2026-10-12. The CSRs' Trace popup
 * for an export is the LYNX one they have used for years (field grid with
 * "updated by this trace" highlights, booking header, Vessel Information /
 * Equipment Information / Equipment In For Booking tables) and that layout is
 * `LegacyTracePanel`, which renders from `LegacyTraceData`. Rather than draw a
 * second export layout, this adapter builds that shape from the gwiTrace
 * snapshot so the same component renders both tracers identically.
 *
 * Two snapshot shapes, because a PRO's link can point at either row of a
 * booking: the placeholder carries a FLAT `export` section; a container row
 * keeps the schedule in the `export.terminal` drawer and promotes vessel and
 * cutoffs to the top level. Read flat, then the drawer, then the top level.
 */

type Dict = Record<string, unknown>;

const obj = (v: unknown): Dict | null =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Dict) : null;
const str = (v: unknown): string => (v == null ? '' : String(v));

const dtf = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Chicago',
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit',
  hourCycle: 'h23',
});

/** CMS-style `YYYY-MM-DD HH:MM:SS` in Central — the format the legacy panel's
 *  values arrive in from cmsapi, so both tracers read the same on screen. */
export const fmtCms = (v: unknown): string => {
  if (v == null || v === '') return '';
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return `${s} 00:00:00`;
  const hasZone = /[Zz]$/.test(s) || /[+-]\d{2}:?\d{2}$/.test(s);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (m && !hasZone) return `${m[1]}-${m[2]}-${m[3]} ${m[4]}:${m[5]}:${m[6] ?? '00'}`;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const p = Object.fromEntries(dtf.formatToParts(d).map((x) => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day} ${p.hour}:${p.minute}:${p.second}`;
};

/** LYNX equipment-type vocabulary from the EVP ISO line. */
const equipType = (e: Dict): string => {
  if (str(e.iso_group).toUpperCase() === 'RE') return 'REEFER';
  if (str(e.container_height) === '96') return 'HIGHCUBE';
  return str(e.iso_type) ? 'STANDARD' : '';
};

/** The export section with content — the signal that this gwiTrace result is
 *  an export booking trace. */
export const isExportTrace = (r: TraceResult): boolean => {
  const exp = obj(r.snapshot?.export);
  return !!exp && Object.keys(exp).length > 0;
};

/** The terminal the trace RAN against — the registration. */
const terminalOf = (s: Dict, drawer: Dict): string =>
  str((obj(s.carriers) ?? {}).terminal ?? drawer.terminal_code);

/**
 * The terminal the PORT works the booking at — the vessel visit's facility
 * (gwiTrace 11340: `facility_scac`, e.g. "HOU-BPT"), read from the flat
 * section or the drawer. Empty until POH has named a visit.
 *
 * Until 2026-10-15 the Vessel Information column printed the registration,
 * which is CMS `rail` echoed back — so on EBKG18962743 the popup said
 * HOU-BCT while the ticket under it said the port had the booking at
 * Bayport, and the LC read the two as a contradiction. The column is the
 * port's answer; "Trace Carrier" stays the terminal we asked.
 */
const portTerminalOf = (exp: Dict, drawer: Dict): string =>
  str(exp.facility_scac ?? drawer.facility_scac);

export const exportTraceData = (r: TraceResult): LegacyTraceData | null => {
  const s = obj(r.snapshot);
  const exp = obj(s?.export);
  if (!s || !exp || Object.keys(exp).length === 0) return null;
  const drawer = obj(exp.terminal) ?? {};
  const ex = (k: string): unknown => exp[k] ?? drawer[k] ?? s[k];
  const terminal = terminalOf(s, drawer);
  const portTerminal = portTerminalOf(exp, drawer) || terminal;

  // The field grid: CMS column names, because that is what `fields_updated`
  // carries and what the panel's label map and highlight keys expect.
  const fields: Record<string, string> = {
    vessel: str(ex('vessel_name')),
    voyage: str(ex('voyage_number')),
    vesselarrivaldateest: fmtCms(ex('vessel_eta') ?? s.estimated_arrival_at),
    vesselsaildateest: fmtCms(ex('etd')),
    firstrcvdate: fmtCms(ex('early_return_date')),
    cutoffdatetime: fmtCms(ex('cargo_cutoff')),
    // The carrier the trace was RUN as -- a Bayport pick on a Barbours Cut
    // order read "HOU-BCT" here (2026-10-19). The registration is the fallback
    // for a backend that does not send it.
    traceruncarrier: str(r.traced_as) || terminal,
    tracerundatetime: fmtCms(r.last_synced_at),
  };
  const updated = [...(r.fields_updated ?? [])];
  if (updated.length > 0) updated.push('tracerundatetime');

  const equipment = Array.isArray(ex('equipment')) ? (ex('equipment') as Dict[]) : [];
  const units = Array.isArray(s.booking_units) ? (s.booking_units as Dict[]) : [];
  const haz = ex('hazardous');

  return {
    fields,
    updated,
    raw: s,
    export_detail: {
      // Category, PreAdvise and Notes are LYNX-only; POH's EVP booking does
      // not publish them. Blank, as they mostly were.
      category: '',
      booking: str(ex('booking_number') ?? s.bill_of_lading),
      line: str(ex('line_scac')),
      hazmat: haz == null ? '' : haz ? 'YES' : 'NO',
      preadvise: '',
      preadvisedate: '',
      notes: '',
      vesselinfo: {
        vessel: str(ex('vessel_name')),
        voyage: str(ex('voyage_number')),
        terminal: portTerminal,
        status: str(ex('visit_phase')),
        pol: str(ex('pol_locode')),
        pod: str(ex('pod_locode')),
        beginrcv: fmtCms(ex('early_return_date')),
        drycutoff: fmtCms(ex('cargo_cutoff')),
        hazcutoff: fmtCms(ex('haz_cutoff')),
        reefercutoff: fmtCms(ex('reefer_cutoff')),
      },
      // Tallies are booking-level in EVP (fulls in / empties out), so they
      // repeat on each equipment line; bookings almost always have one.
      equipinfo: equipment.map((e) => ({
        qty: str(e.qty ?? ex('quantity')),
        mty: str(ex('tally_out')),
        rcvd: str(ex('tally_in')),
        size: str(e.container_length),
        type: equipType(e),
      })),
      equipin: units.map((u) => ({
        equipment: str(u.container_number),
        size: '',
        type: str(u.equipment_type ?? u.lifecycle_state),
        position: str(u.current_location ?? u.grounded_spot),
      })),
    },
  };
};

/** The legacy status line — "Traced on HOU-BCT: Update Export" — for the
 *  gwiTrace lane, so the row reads the same whichever tracer answered. */
export const exportSummary = (r: TraceResult): string => {
  const s = obj(r.snapshot) ?? {};
  // What the LC traced with, as "Trace Carrier" above; the registration only
  // as a fallback.
  const terminal = str(r.traced_as) || terminalOf(s, obj(obj(s.export)?.terminal) ?? {});
  // 'stored' is the snapshot the popup opened on while the port is asked:
  // not a verdict, so it must not read as "No update" for the seconds it
  // shows. The real result replaces it in place.
  if (r.status === 'stored') return terminal ? `Last sync on ${terminal}` : 'Last sync';
  if (r.status === 'not_found') return r.summary;
  const what =
    r.status === 'updated'
      ? 'Update Export'
      : r.status === 'registered'
        ? 'Registered'
        : r.status === 'error'
          ? r.summary
          : 'No update';
  return terminal ? `Traced on ${terminal}: ${what}` : what;
};
